import { useThree } from '@react-three/fiber'
import { useCallback, useEffect } from 'react'
import * as THREE from 'three'

import { useZoomContext, type ZOOM_STATE } from '@routes/Panorama/ZoomContext'

import type { PanoramaImage } from '@state/panorama.ts'

const DEFAULT_ZOOM_ANGLE = 75
const MIN_ZOOM_ANGLE = 30
const MAX_ZOOM_ANGLE = 110
const ZOOM_STEP = 5

interface FovZoomProps {
  panoramaImage?: PanoramaImage
}

function FovZoom({ panoramaImage }: FovZoomProps) {
  const { registerZoomHandles, notifyZoomChange } = useZoomContext()
  const three = useThree()
  const camera = three.camera as THREE.PerspectiveCamera
  const gl = three.gl

  const updateFov = useCallback((newFov: number) => {
    // eslint-disable-next-line react-hooks/immutability
    camera.fov = THREE.MathUtils.clamp(newFov, MIN_ZOOM_ANGLE, MAX_ZOOM_ANGLE)
    camera.updateProjectionMatrix()
  }, [camera])

  // reset FOV when panorama image changes
  useEffect(() => {
    updateFov(DEFAULT_ZOOM_ANGLE)
    notifyZoomChange()
  }, [updateFov, notifyZoomChange, panoramaImage?.id])

  // register zoom handles for external UI controls
  useEffect(() => {
    registerZoomHandles({
      zoomIn: () => updateFov(camera.fov - ZOOM_STEP),
      zoomOut: () => updateFov(camera.fov + ZOOM_STEP),
      resetZoom: () => updateFov(DEFAULT_ZOOM_ANGLE),
      isZoomed: () => Math.abs(camera.fov - DEFAULT_ZOOM_ANGLE) > 0.1,
      getZoomState: (): ZOOM_STATE => {
        switch (camera.fov) {
          case MIN_ZOOM_ANGLE: return 'MIN'
          case MAX_ZOOM_ANGLE: return 'MAX'
          default: return 'DEFAULT'
        }
      },
    })
  }, [registerZoomHandles, updateFov, camera])

  // update the FOV based on mouse wheel to simulate zooming in and out
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      updateFov(camera.fov + event.deltaY * 0.05)
      notifyZoomChange()
    }

    const canvasElement = gl.domElement
    canvasElement.addEventListener('wheel', handleWheel, { passive: true })

    return () => canvasElement.removeEventListener('wheel', handleWheel)
  }, [updateFov, notifyZoomChange, camera, gl])

  return null
}

export default FovZoom